import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';
import Footer from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { 
  FileText, Plus, Trash2, Download, Eye, Building2, History, Search, Calendar, RefreshCw, Save
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_467e7424-249d-4984-8afc-452f42a05c3c/artifacts/6ivxke97_Logo.png';

// Helper to get end of current month
const getEndOfMonth = () => {
  const now = new Date();
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return lastDay.toISOString().split('T')[0];
};

// Helper to get start of current month
const getStartOfMonth = () => {
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
  return firstDay.toISOString().split('T')[0];
};

const InvoiceGenerator = ({ user, onLogout }) => {
  const [invoiceData, setInvoiceData] = useState({
    invoiceNo: '001',
    invoiceDate: new Date().toISOString().split('T')[0],
    dueDate: getEndOfMonth(),
    servicePeriodStart: getStartOfMonth(),
    servicePeriodEnd: getEndOfMonth(),
    billTo: {
      companyName: '',
      address: '',
      contactPerson: '',
      email: '',
      phone: ''
    },
    paymentTerms: 'Monthly Net 45',
    services: [
      { description: '', quantity: 1, rate: 0, amount: 0 }
    ],
    bankDetails: {
      paymentMethod: 'ACH Transfer',
      bankName: 'Citi Bank',
      beneficiaryName: 'Creative Infotech',
      routingNumber: '031100209',
      accountNumber: '70587920001522073',
      accountType: 'Checking',
      swiftCode: 'CITIUS33',
      bankAddress: '111 Wall Street, New York, NY 10043, USA'
    },
    contactInfo: {
      email: 'info@creativeinfotech.net',
      phone: '+91-9510491784',
      contactPerson: 'Ashutosh Mehta, Director',
      payoneer: 'creativeinfotech9991@gmail.com',
      paypal: 'creativeinfotech9696@gmail.com'
    },
    footerMessage: 'Thank You for business partnership!'
  });

  const [showPreview, setShowPreview] = useState(false);
  const [showDownloadDialog, setShowDownloadDialog] = useState(false);
  const [fileName, setFileName] = useState('Invoice');
  const [generating, setGenerating] = useState(false);
  const invoiceRef = useRef(null);
  
  // History states
  const [activeTab, setActiveTab] = useState('create'); // 'create' or 'history'
  const [invoiceHistory, setInvoiceHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [startDateFilter, setStartDateFilter] = useState('');
  const [endDateFilter, setEndDateFilter] = useState('');
  const [selectedHistoryInvoice, setSelectedHistoryInvoice] = useState(null);
  const [saving, setSaving] = useState(false);

  // Fetch invoice history
  const fetchInvoiceHistory = async () => {
    setHistoryLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = {};
      if (searchQuery) params.search = searchQuery;
      if (startDateFilter) params.start_date = startDateFilter;
      if (endDateFilter) params.end_date = endDateFilter;
      
      const response = await axios.get(`${BACKEND_URL}/api/invoices`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setInvoiceHistory(response.data.invoices || []);
    } catch (error) {
      toast.error('Failed to fetch invoice history');
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'history') {
      fetchInvoiceHistory();
    }
  }, [activeTab]);

  // Save invoice to database
  const saveInvoice = async () => {
    if (!invoiceData.billTo.companyName) {
      toast.error('Please fill in the Bill To company name');
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${BACKEND_URL}/api/invoices`, {
        invoice_no: invoiceData.invoiceNo,
        invoice_date: invoiceData.invoiceDate,
        due_date: invoiceData.dueDate,
        service_period_start: invoiceData.servicePeriodStart,
        service_period_end: invoiceData.servicePeriodEnd,
        payment_terms: invoiceData.paymentTerms,
        bill_to: invoiceData.billTo,
        services: invoiceData.services,
        bank_details: invoiceData.bankDetails,
        contact_info: invoiceData.contactInfo,
        footer_message: invoiceData.footerMessage,
        total_amount: calculateTotal()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Invoice saved successfully!');
    } catch (error) {
      toast.error('Failed to save invoice');
    } finally {
      setSaving(false);
    }
  };

  // Load invoice from history
  const loadInvoice = (invoice) => {
    setInvoiceData({
      invoiceNo: invoice.invoice_no,
      invoiceDate: invoice.invoice_date,
      dueDate: invoice.due_date,
      servicePeriodStart: invoice.service_period_start,
      servicePeriodEnd: invoice.service_period_end,
      billTo: invoice.bill_to,
      paymentTerms: invoice.payment_terms,
      services: invoice.services,
      bankDetails: invoice.bank_details,
      contactInfo: invoice.contact_info,
      footerMessage: invoice.footer_message
    });
    setActiveTab('create');
    toast.success('Invoice loaded for editing');
  };

  // Delete invoice
  const deleteInvoice = async (invoiceId) => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/invoices/${invoiceId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Invoice deleted');
      fetchInvoiceHistory();
    } catch (error) {
      toast.error('Failed to delete invoice');
    }
  };

  const updateBillTo = (field, value) => {
    setInvoiceData(prev => ({
      ...prev,
      billTo: { ...prev.billTo, [field]: value }
    }));
  };

  const updateBankDetails = (field, value) => {
    setInvoiceData(prev => ({
      ...prev,
      bankDetails: { ...prev.bankDetails, [field]: value }
    }));
  };

  const addService = () => {
    setInvoiceData(prev => ({
      ...prev,
      services: [...prev.services, { description: '', quantity: 1, rate: 0, amount: 0 }]
    }));
  };

  const removeService = (index) => {
    if (invoiceData.services.length > 1) {
      setInvoiceData(prev => ({
        ...prev,
        services: prev.services.filter((_, i) => i !== index)
      }));
    }
  };

  const updateService = (index, field, value) => {
    setInvoiceData(prev => {
      const services = [...prev.services];
      services[index] = { ...services[index], [field]: value };
      
      // Auto-calculate amount
      if (field === 'quantity' || field === 'rate') {
        const qty = field === 'quantity' ? parseFloat(value) || 0 : parseFloat(services[index].quantity) || 0;
        const rate = field === 'rate' ? parseFloat(value) || 0 : parseFloat(services[index].rate) || 0;
        services[index].amount = qty * rate;
      }
      
      return { ...prev, services };
    });
  };

  const calculateTotal = () => {
    return invoiceData.services.reduce((sum, service) => sum + (parseFloat(service.amount) || 0), 0);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const formatDateLong = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: '2-digit' });
  };

  const formatDateShort = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: '2-digit' });
  };

  const generatePDF = async () => {
    setGenerating(true);
    try {
      // Dynamic import of html2pdf
      const html2pdf = (await import('html2pdf.js')).default;
      
      const element = document.getElementById('invoice-preview-content');
      const opt = {
        margin: [5, 10, 5, 10],
        filename: `${fileName}.pdf`,
        image: { type: 'jpeg', quality: 0.95 },
        html2canvas: { 
          scale: 2, 
          useCORS: true,
          allowTaint: true,
          logging: false,
          letterRendering: true
        },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: 'avoid-all', before: [], after: [], avoid: ['tr', 'div'] }
      };
      
      await html2pdf().set(opt).from(element).save();
      toast.success('Invoice downloaded successfully!');
      setShowDownloadDialog(false);
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('Failed to generate PDF. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const InvoicePreviewContent = () => (
    <div 
      id="invoice-preview-content" 
      style={{ 
        width: '190mm', 
        minHeight: '277mm',
        maxHeight: '277mm',
        padding: '8mm',
        backgroundColor: 'white',
        color: 'black',
        fontFamily: 'Arial, sans-serif',
        fontSize: '11px',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        overflow: 'hidden'
      }}
    >
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start', 
        paddingBottom: '8px', 
        marginBottom: '10px', 
        borderBottom: '2px solid #f59e0b' 
      }}>
        <div>
          <h1 style={{ fontSize: '22px', fontWeight: 'bold', color: '#1e293b', margin: '0 0 4px 0' }}>INVOICE</h1>
          <p style={{ color: '#64748b', fontSize: '11px', margin: '2px 0' }}>Invoice No: <span style={{ fontWeight: '600', color: '#334155' }}>{invoiceData.invoiceNo}</span></p>
          <p style={{ color: '#64748b', fontSize: '11px', margin: '2px 0' }}>Date: <span style={{ fontWeight: '600', color: '#334155' }}>{formatDateLong(invoiceData.invoiceDate)}</span></p>
        </div>
        <div style={{ textAlign: 'right' }}>
          {/* Company Logo and Name */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px', marginBottom: '4px' }}>
            <div style={{ 
              width: '36px', 
              height: '36px', 
              backgroundColor: '#f59e0b', 
              borderRadius: '6px', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              color: 'white',
              fontWeight: 'bold',
              fontSize: '14px'
            }}>CI</div>
            <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#1e293b' }}>Creative Infotech</span>
          </div>
          {/* Company Address */}
          <p style={{ color: '#475569', fontSize: '9px', margin: '1px 0' }}>717, Fortune Business Hub,</p>
          <p style={{ color: '#475569', fontSize: '9px', margin: '1px 0' }}>Science City Road, Ahmedabad,</p>
          <p style={{ color: '#475569', fontSize: '9px', margin: '1px 0' }}>Gujarat, INDIA - 380060</p>
          <p style={{ color: '#475569', fontSize: '9px', margin: '1px 0' }}>Email: info@creativeinfotech.net</p>
        </div>
      </div>

      {/* Payment Terms & Bill To - Side by Side */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '10px' }}>
        {/* Payment Terms */}
        <div style={{ flex: '1', backgroundColor: '#f8fafc', padding: '8px', borderRadius: '4px' }}>
          <h3 style={{ fontSize: '9px', fontWeight: '600', color: '#f59e0b', textTransform: 'uppercase', margin: '0 0 6px 0' }}>Payment Terms</h3>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
            <div>
              <p style={{ color: '#64748b', fontSize: '9px', margin: '0' }}>Terms</p>
              <p style={{ fontWeight: '600', color: '#1e293b', fontSize: '10px', margin: '2px 0 0 0' }}>{invoiceData.paymentTerms}</p>
            </div>
            <div>
              <p style={{ color: '#64748b', fontSize: '9px', margin: '0' }}>Due Date</p>
              <p style={{ fontWeight: '600', color: '#1e293b', fontSize: '10px', margin: '2px 0 0 0' }}>{formatDateLong(invoiceData.dueDate)}</p>
            </div>
            <div>
              <p style={{ color: '#64748b', fontSize: '9px', margin: '0' }}>Service Period</p>
              <p style={{ fontWeight: '600', color: '#1e293b', fontSize: '9px', margin: '2px 0 0 0' }}>{formatDateShort(invoiceData.servicePeriodStart)} - {formatDateShort(invoiceData.servicePeriodEnd)}</p>
            </div>
          </div>
        </div>

        {/* Bill To */}
        <div style={{ flex: '1', backgroundColor: '#f8fafc', padding: '8px', borderRadius: '4px' }}>
          <h3 style={{ fontSize: '9px', fontWeight: '600', color: '#f59e0b', textTransform: 'uppercase', margin: '0 0 6px 0' }}>Bill To</h3>
          <p style={{ fontWeight: 'bold', color: '#1e293b', fontSize: '11px', margin: '0 0 2px 0' }}>{invoiceData.billTo.companyName || 'Company Name'}</p>
          <p style={{ color: '#475569', fontSize: '9px', margin: '0', whiteSpace: 'pre-line' }}>{invoiceData.billTo.address || 'Address'}</p>
          {invoiceData.billTo.contactPerson && <p style={{ color: '#475569', fontSize: '9px', margin: '2px 0 0 0' }}>Contact: {invoiceData.billTo.contactPerson}</p>}
          {invoiceData.billTo.email && <p style={{ color: '#475569', fontSize: '9px', margin: '0' }}>Email: {invoiceData.billTo.email}</p>}
          {invoiceData.billTo.phone && <p style={{ color: '#475569', fontSize: '9px', margin: '0' }}>Phone: {invoiceData.billTo.phone}</p>}
        </div>
      </div>

      {/* Services Table */}
      <div style={{ marginBottom: '10px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
          <thead>
            <tr style={{ backgroundColor: '#1e293b', color: 'white' }}>
              <th style={{ textAlign: 'left', padding: '6px 8px', borderTopLeftRadius: '4px', fontSize: '10px' }}>Service Description</th>
              <th style={{ textAlign: 'center', padding: '6px 8px', width: '50px', fontSize: '10px' }}>Qty</th>
              <th style={{ textAlign: 'right', padding: '6px 8px', width: '70px', fontSize: '10px' }}>Rate</th>
              <th style={{ textAlign: 'right', padding: '6px 8px', width: '80px', borderTopRightRadius: '4px', fontSize: '10px' }}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {invoiceData.services.map((service, index) => (
              <tr key={index} style={{ backgroundColor: index % 2 === 0 ? '#f8fafc' : 'white' }}>
                <td style={{ padding: '6px 8px', borderBottom: '1px solid #e2e8f0', fontSize: '10px' }}>{service.description || '-'}</td>
                <td style={{ padding: '6px 8px', borderBottom: '1px solid #e2e8f0', textAlign: 'center', fontSize: '10px' }}>{service.quantity}</td>
                <td style={{ padding: '6px 8px', borderBottom: '1px solid #e2e8f0', textAlign: 'right', fontSize: '10px' }}>{formatCurrency(service.rate)}</td>
                <td style={{ padding: '6px 8px', borderBottom: '1px solid #e2e8f0', textAlign: 'right', fontWeight: '600', fontSize: '10px' }}>{formatCurrency(service.amount)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ backgroundColor: '#f59e0b', color: 'white' }}>
              <td colSpan="3" style={{ padding: '8px', textAlign: 'right', fontWeight: 'bold', borderBottomLeftRadius: '4px', fontSize: '11px' }}>Total Amount:</td>
              <td style={{ padding: '8px', textAlign: 'right', fontWeight: 'bold', fontSize: '14px', borderBottomRightRadius: '4px' }}>{formatCurrency(calculateTotal())}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Bank Details - Compact 2-row layout */}
      <div style={{ marginBottom: '10px' }}>
        <h3 style={{ fontSize: '9px', fontWeight: '600', color: '#f59e0b', textTransform: 'uppercase', margin: '0 0 6px 0' }}>Payment Information</h3>
        <div style={{ backgroundColor: '#f8fafc', padding: '8px', borderRadius: '4px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
            <div style={{ flex: '1' }}>
              <p style={{ color: '#64748b', fontSize: '8px', margin: '0' }}>Payment Method</p>
              <p style={{ fontWeight: '600', color: '#1e293b', fontSize: '10px', margin: '1px 0 0 0' }}>{invoiceData.bankDetails.paymentMethod}</p>
            </div>
            <div style={{ flex: '1' }}>
              <p style={{ color: '#64748b', fontSize: '8px', margin: '0' }}>Bank Name</p>
              <p style={{ fontWeight: '600', color: '#1e293b', fontSize: '10px', margin: '1px 0 0 0' }}>{invoiceData.bankDetails.bankName}</p>
            </div>
            <div style={{ flex: '1' }}>
              <p style={{ color: '#64748b', fontSize: '8px', margin: '0' }}>Beneficiary</p>
              <p style={{ fontWeight: '600', color: '#1e293b', fontSize: '10px', margin: '1px 0 0 0' }}>{invoiceData.bankDetails.beneficiaryName}</p>
            </div>
            <div style={{ flex: '1' }}>
              <p style={{ color: '#64748b', fontSize: '8px', margin: '0' }}>Account Type</p>
              <p style={{ fontWeight: '600', color: '#1e293b', fontSize: '10px', margin: '1px 0 0 0' }}>{invoiceData.bankDetails.accountType}</p>
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div style={{ flex: '1' }}>
              <p style={{ color: '#64748b', fontSize: '8px', margin: '0' }}>Routing Number</p>
              <p style={{ fontWeight: '600', color: '#1e293b', fontSize: '10px', margin: '1px 0 0 0' }}>{invoiceData.bankDetails.routingNumber}</p>
            </div>
            <div style={{ flex: '1' }}>
              <p style={{ color: '#64748b', fontSize: '8px', margin: '0' }}>Account Number</p>
              <p style={{ fontWeight: '600', color: '#1e293b', fontSize: '10px', margin: '1px 0 0 0' }}>{invoiceData.bankDetails.accountNumber}</p>
            </div>
            <div style={{ flex: '1' }}>
              <p style={{ color: '#64748b', fontSize: '8px', margin: '0' }}>SWIFT Code</p>
              <p style={{ fontWeight: '600', color: '#1e293b', fontSize: '10px', margin: '1px 0 0 0' }}>{invoiceData.bankDetails.swiftCode}</p>
            </div>
            <div style={{ flex: '1' }}>
              <p style={{ color: '#64748b', fontSize: '8px', margin: '0' }}>Bank Address</p>
              <p style={{ fontWeight: '600', color: '#1e293b', fontSize: '9px', margin: '1px 0 0 0' }}>{invoiceData.bankDetails.bankAddress}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Information - Compact horizontal layout */}
      <div style={{ marginBottom: '10px' }}>
        <h3 style={{ fontSize: '9px', fontWeight: '600', color: '#f59e0b', textTransform: 'uppercase', margin: '0 0 6px 0' }}>Contact Information</h3>
        <div style={{ backgroundColor: '#f8fafc', padding: '8px', borderRadius: '4px', display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
          <div style={{ flex: '1' }}>
            <p style={{ color: '#64748b', fontSize: '8px', margin: '0' }}>Email</p>
            <p style={{ fontWeight: '600', color: '#1e293b', fontSize: '9px', margin: '1px 0 0 0' }}>{invoiceData.contactInfo.email}</p>
          </div>
          <div style={{ flex: '1' }}>
            <p style={{ color: '#64748b', fontSize: '8px', margin: '0' }}>Phone</p>
            <p style={{ fontWeight: '600', color: '#1e293b', fontSize: '9px', margin: '1px 0 0 0' }}>{invoiceData.contactInfo.phone}</p>
          </div>
          <div style={{ flex: '1' }}>
            <p style={{ color: '#64748b', fontSize: '8px', margin: '0' }}>Contact</p>
            <p style={{ fontWeight: '600', color: '#1e293b', fontSize: '9px', margin: '1px 0 0 0' }}>{invoiceData.contactInfo.contactPerson}</p>
          </div>
          <div style={{ flex: '1' }}>
            <p style={{ color: '#64748b', fontSize: '8px', margin: '0' }}>Payoneer</p>
            <p style={{ fontWeight: '600', color: '#1e293b', fontSize: '8px', margin: '1px 0 0 0' }}>{invoiceData.contactInfo.payoneer}</p>
          </div>
          <div style={{ flex: '1' }}>
            <p style={{ color: '#64748b', fontSize: '8px', margin: '0' }}>PayPal</p>
            <p style={{ fontWeight: '600', color: '#1e293b', fontSize: '8px', margin: '1px 0 0 0' }}>{invoiceData.contactInfo.paypal}</p>
          </div>
        </div>
      </div>

      {/* Footer - pushed to bottom with flex-grow spacer */}
      <div style={{ flexGrow: '1' }}></div>
      <div style={{ textAlign: 'center', paddingTop: '8px', borderTop: '1px solid #e2e8f0' }}>
        <p style={{ color: '#f59e0b', fontWeight: '600', fontSize: '12px', margin: '0' }}>{invoiceData.footerMessage}</p>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <Sidebar user={user} onLogout={onLogout} />
      <main className="flex-1 p-6 overflow-auto" data-testid="invoice-generator">
        <div className="max-w-6xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center shadow-lg shadow-amber-500/20">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">Invoice Generator</h1>
                <p className="text-slate-400">Create, save and manage invoices</p>
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="flex gap-4 border-b border-slate-700">
            <button
              onClick={() => setActiveTab('create')}
              className={`pb-3 px-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'create'
                  ? 'border-amber-500 text-amber-500'
                  : 'border-transparent text-slate-400 hover:text-slate-300'
              }`}
            >
              <FileText className="w-4 h-4 inline mr-2" />
              Create Invoice
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`pb-3 px-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'history'
                  ? 'border-amber-500 text-amber-500'
                  : 'border-transparent text-slate-400 hover:text-slate-300'
              }`}
            >
              <History className="w-4 h-4 inline mr-2" />
              Invoice History
            </button>
          </div>

          {/* Create Invoice Tab */}
          {activeTab === 'create' && (
            <>
              {/* Action Buttons */}
              <div className="flex gap-2 justify-end">
                <Button
                  variant="outline"
                  onClick={saveInvoice}
                  disabled={saving}
                  className="border-emerald-500 text-emerald-500 hover:bg-emerald-500/10"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Invoice'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowPreview(true)}
                  className="border-slate-600 text-slate-300 hover:bg-slate-700"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Preview
                </Button>
                <Button
                  onClick={() => {
                    setFileName(`Invoice_${invoiceData.invoiceNo}_${invoiceData.billTo.companyName || 'Client'}`);
                    setShowDownloadDialog(true);
                  }}
                  className="bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-700 hover:to-amber-600"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download PDF
                </Button>
              </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Invoice Details */}
            <Card className="bg-gradient-to-br from-slate-800/80 to-slate-800/40 border-slate-700/50 shadow-lg">
              <CardHeader className="border-b border-slate-700/50">
                <CardTitle className="text-white flex items-center gap-2">
                  <FileText className="w-5 h-5 text-amber-500" />
                  Invoice Details
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Invoice No.</Label>
                    <Input
                      value={invoiceData.invoiceNo}
                      onChange={(e) => setInvoiceData(prev => ({ ...prev, invoiceNo: e.target.value }))}
                      className="bg-slate-900/50 border-slate-600 text-white"
                      placeholder="001"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Invoice Date</Label>
                    <Input
                      type="date"
                      value={invoiceData.invoiceDate}
                      onChange={(e) => setInvoiceData(prev => ({ ...prev, invoiceDate: e.target.value }))}
                      className="bg-slate-900/50 border-slate-600 text-white"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Payment Terms</Label>
                    <Input
                      value={invoiceData.paymentTerms}
                      onChange={(e) => setInvoiceData(prev => ({ ...prev, paymentTerms: e.target.value }))}
                      className="bg-slate-900/50 border-slate-600 text-white"
                      placeholder="Monthly Net 45"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Due Date</Label>
                    <Input
                      type="date"
                      value={invoiceData.dueDate}
                      onChange={(e) => setInvoiceData(prev => ({ ...prev, dueDate: e.target.value }))}
                      className="bg-slate-900/50 border-slate-600 text-white"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Service Period Start</Label>
                    <Input
                      type="date"
                      value={invoiceData.servicePeriodStart}
                      onChange={(e) => setInvoiceData(prev => ({ ...prev, servicePeriodStart: e.target.value }))}
                      className="bg-slate-900/50 border-slate-600 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Service Period End</Label>
                    <Input
                      type="date"
                      value={invoiceData.servicePeriodEnd}
                      onChange={(e) => setInvoiceData(prev => ({ ...prev, servicePeriodEnd: e.target.value }))}
                      className="bg-slate-900/50 border-slate-600 text-white"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Bill To */}
            <Card className="bg-gradient-to-br from-slate-800/80 to-slate-800/40 border-slate-700/50 shadow-lg">
              <CardHeader className="border-b border-slate-700/50">
                <CardTitle className="text-white flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-amber-500" />
                  Bill To
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-4">
                <div>
                  <Label className="text-slate-300">Company Name</Label>
                  <Input
                    value={invoiceData.billTo.companyName}
                    onChange={(e) => updateBillTo('companyName', e.target.value)}
                    className="bg-slate-900/50 border-slate-600 text-white"
                    placeholder="Client Company Name"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Address</Label>
                  <Textarea
                    value={invoiceData.billTo.address}
                    onChange={(e) => updateBillTo('address', e.target.value)}
                    className="bg-slate-900/50 border-slate-600 text-white"
                    placeholder="Full address"
                    rows={2}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Contact Person</Label>
                    <Input
                      value={invoiceData.billTo.contactPerson}
                      onChange={(e) => updateBillTo('contactPerson', e.target.value)}
                      className="bg-slate-900/50 border-slate-600 text-white"
                      placeholder="Mr./Ms. Name"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Phone</Label>
                    <Input
                      value={invoiceData.billTo.phone}
                      onChange={(e) => updateBillTo('phone', e.target.value)}
                      className="bg-slate-900/50 border-slate-600 text-white"
                      placeholder="+1 234 567 890"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-slate-300">Email</Label>
                  <Input
                    value={invoiceData.billTo.email}
                    onChange={(e) => updateBillTo('email', e.target.value)}
                    className="bg-slate-900/50 border-slate-600 text-white"
                    placeholder="client@example.com"
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Services */}
          <Card className="bg-gradient-to-br from-slate-800/80 to-slate-800/40 border-slate-700/50 shadow-lg">
            <CardHeader className="border-b border-slate-700/50 flex flex-row items-center justify-between">
              <CardTitle className="text-white">Service Details</CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={addService}
                className="border-amber-500 text-amber-500 hover:bg-amber-500/10"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Service
              </Button>
            </CardHeader>
            <CardContent className="p-4">
              <div className="space-y-3">
                {invoiceData.services.map((service, index) => (
                  <div key={index} className="grid grid-cols-12 gap-3 items-end">
                    <div className="col-span-5">
                      {index === 0 && <Label className="text-slate-300 text-xs">Description</Label>}
                      <Input
                        value={service.description}
                        onChange={(e) => updateService(index, 'description', e.target.value)}
                        className="bg-slate-900/50 border-slate-600 text-white"
                        placeholder="Service description"
                      />
                    </div>
                    <div className="col-span-2">
                      {index === 0 && <Label className="text-slate-300 text-xs">Quantity</Label>}
                      <Input
                        type="number"
                        value={service.quantity}
                        onChange={(e) => updateService(index, 'quantity', e.target.value)}
                        className="bg-slate-900/50 border-slate-600 text-white"
                        min="1"
                      />
                    </div>
                    <div className="col-span-2">
                      {index === 0 && <Label className="text-slate-300 text-xs">Rate ($)</Label>}
                      <Input
                        type="number"
                        value={service.rate}
                        onChange={(e) => updateService(index, 'rate', e.target.value)}
                        className="bg-slate-900/50 border-slate-600 text-white"
                        step="0.01"
                        min="0"
                      />
                    </div>
                    <div className="col-span-2">
                      {index === 0 && <Label className="text-slate-300 text-xs">Amount</Label>}
                      <Input
                        value={formatCurrency(service.amount)}
                        className="bg-slate-700/50 border-slate-600 text-amber-400 font-semibold"
                        readOnly
                      />
                    </div>
                    <div className="col-span-1">
                      {invoiceData.services.length > 1 && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeService(index)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t border-slate-700 flex justify-end">
                <div className="bg-amber-500/20 px-6 py-3 rounded-lg">
                  <span className="text-slate-300 mr-4">Total:</span>
                  <span className="text-2xl font-bold text-amber-400">{formatCurrency(calculateTotal())}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Bank Details */}
          <Card className="bg-gradient-to-br from-slate-800/80 to-slate-800/40 border-slate-700/50 shadow-lg">
            <CardHeader className="border-b border-slate-700/50">
              <CardTitle className="text-white">Bank Details</CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <Label className="text-slate-300">Payment Method</Label>
                  <Input
                    value={invoiceData.bankDetails.paymentMethod}
                    onChange={(e) => updateBankDetails('paymentMethod', e.target.value)}
                    className="bg-slate-900/50 border-slate-600 text-white"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Bank Name</Label>
                  <Input
                    value={invoiceData.bankDetails.bankName}
                    onChange={(e) => updateBankDetails('bankName', e.target.value)}
                    className="bg-slate-900/50 border-slate-600 text-white"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Beneficiary Name</Label>
                  <Input
                    value={invoiceData.bankDetails.beneficiaryName}
                    onChange={(e) => updateBankDetails('beneficiaryName', e.target.value)}
                    className="bg-slate-900/50 border-slate-600 text-white"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Account Type</Label>
                  <Input
                    value={invoiceData.bankDetails.accountType}
                    onChange={(e) => updateBankDetails('accountType', e.target.value)}
                    className="bg-slate-900/50 border-slate-600 text-white"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Routing Number</Label>
                  <Input
                    value={invoiceData.bankDetails.routingNumber}
                    onChange={(e) => updateBankDetails('routingNumber', e.target.value)}
                    className="bg-slate-900/50 border-slate-600 text-white"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Account Number</Label>
                  <Input
                    value={invoiceData.bankDetails.accountNumber}
                    onChange={(e) => updateBankDetails('accountNumber', e.target.value)}
                    className="bg-slate-900/50 border-slate-600 text-white"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">SWIFT Code</Label>
                  <Input
                    value={invoiceData.bankDetails.swiftCode}
                    onChange={(e) => updateBankDetails('swiftCode', e.target.value)}
                    className="bg-slate-900/50 border-slate-600 text-white"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Bank Address</Label>
                  <Input
                    value={invoiceData.bankDetails.bankAddress}
                    onChange={(e) => updateBankDetails('bankAddress', e.target.value)}
                    className="bg-slate-900/50 border-slate-600 text-white"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <Card className="bg-gradient-to-br from-slate-800/80 to-slate-800/40 border-slate-700/50 shadow-lg">
            <CardHeader className="border-b border-slate-700/50">
              <CardTitle className="text-white">Contact Information</CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                <div>
                  <Label className="text-slate-300">Email</Label>
                  <Input
                    value={invoiceData.contactInfo.email}
                    onChange={(e) => setInvoiceData(prev => ({ ...prev, contactInfo: { ...prev.contactInfo, email: e.target.value } }))}
                    className="bg-slate-900/50 border-slate-600 text-white"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Phone</Label>
                  <Input
                    value={invoiceData.contactInfo.phone}
                    onChange={(e) => setInvoiceData(prev => ({ ...prev, contactInfo: { ...prev.contactInfo, phone: e.target.value } }))}
                    className="bg-slate-900/50 border-slate-600 text-white"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Contact Person</Label>
                  <Input
                    value={invoiceData.contactInfo.contactPerson}
                    onChange={(e) => setInvoiceData(prev => ({ ...prev, contactInfo: { ...prev.contactInfo, contactPerson: e.target.value } }))}
                    className="bg-slate-900/50 border-slate-600 text-white"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Payoneer</Label>
                  <Input
                    value={invoiceData.contactInfo.payoneer}
                    onChange={(e) => setInvoiceData(prev => ({ ...prev, contactInfo: { ...prev.contactInfo, payoneer: e.target.value } }))}
                    className="bg-slate-900/50 border-slate-600 text-white"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">PayPal</Label>
                  <Input
                    value={invoiceData.contactInfo.paypal}
                    onChange={(e) => setInvoiceData(prev => ({ ...prev, contactInfo: { ...prev.contactInfo, paypal: e.target.value } }))}
                    className="bg-slate-900/50 border-slate-600 text-white"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Footer Message */}
          <Card className="bg-gradient-to-br from-slate-800/80 to-slate-800/40 border-slate-700/50 shadow-lg">
            <CardContent className="p-4">
              <Label className="text-slate-300">Footer Message</Label>
              <Input
                value={invoiceData.footerMessage}
                onChange={(e) => setInvoiceData(prev => ({ ...prev, footerMessage: e.target.value }))}
                className="bg-slate-900/50 border-slate-600 text-white mt-2"
                placeholder="Thank you message..."
              />
            </CardContent>
          </Card>
            </>
          )}

          {/* Invoice History Tab */}
          {activeTab === 'history' && (
            <Card className="bg-gradient-to-br from-slate-800/80 to-slate-800/40 border-slate-700/50 shadow-lg">
              <CardHeader className="border-b border-slate-700/50">
                <CardTitle className="text-white flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <History className="w-5 h-5 text-amber-500" />
                    Invoice History
                  </span>
                  <Badge variant="outline" className="text-slate-400 border-slate-600">
                    {invoiceHistory.length} invoices
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                {/* Filters */}
                <div className="flex flex-wrap gap-4 mb-4">
                  <div className="flex-1 min-w-[200px]">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                      <Input
                        placeholder="Search by company or invoice no..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10 bg-slate-900/50 border-slate-600 text-white"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2 items-center">
                    <Calendar className="w-4 h-4 text-slate-500" />
                    <Input
                      type="date"
                      value={startDateFilter}
                      onChange={(e) => setStartDateFilter(e.target.value)}
                      className="bg-slate-900/50 border-slate-600 text-white w-40"
                      placeholder="Start Date"
                    />
                    <span className="text-slate-500">to</span>
                    <Input
                      type="date"
                      value={endDateFilter}
                      onChange={(e) => setEndDateFilter(e.target.value)}
                      className="bg-slate-900/50 border-slate-600 text-white w-40"
                      placeholder="End Date"
                    />
                  </div>
                  <Button
                    variant="outline"
                    onClick={fetchInvoiceHistory}
                    className="border-slate-600 text-slate-300 hover:bg-slate-700"
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${historyLoading ? 'animate-spin' : ''}`} />
                    Search
                  </Button>
                </div>

                {/* Invoice Table */}
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-b border-slate-700/50 bg-slate-800/50">
                        <TableHead className="text-slate-400">Invoice No.</TableHead>
                        <TableHead className="text-slate-400">Date</TableHead>
                        <TableHead className="text-slate-400">Bill To</TableHead>
                        <TableHead className="text-slate-400">Service Period</TableHead>
                        <TableHead className="text-slate-400 text-right">Amount</TableHead>
                        <TableHead className="text-slate-400 text-center">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {historyLoading ? (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center py-8">
                            <div className="flex items-center justify-center gap-2 text-slate-400">
                              <RefreshCw className="w-5 h-5 animate-spin" />
                              Loading invoices...
                            </div>
                          </TableCell>
                        </TableRow>
                      ) : invoiceHistory.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center py-8 text-slate-400">
                            No invoices found. Create your first invoice!
                          </TableCell>
                        </TableRow>
                      ) : (
                        invoiceHistory.map((invoice) => (
                          <TableRow key={invoice.id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                            <TableCell className="text-white font-medium">{invoice.invoice_no}</TableCell>
                            <TableCell className="text-slate-300">
                              {new Date(invoice.invoice_date).toLocaleDateString()}
                            </TableCell>
                            <TableCell className="text-slate-300">
                              {invoice.bill_to?.companyName || '-'}
                            </TableCell>
                            <TableCell className="text-slate-400 text-sm">
                              {new Date(invoice.service_period_start).toLocaleDateString()} - {new Date(invoice.service_period_end).toLocaleDateString()}
                            </TableCell>
                            <TableCell className="text-amber-400 text-right font-semibold">
                              ${invoice.total_amount?.toLocaleString() || '0'}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center justify-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedHistoryInvoice(invoice);
                                    setShowPreview(true);
                                  }}
                                  className="text-slate-400 hover:text-white hover:bg-slate-700"
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => loadInvoice(invoice)}
                                  className="text-amber-500 hover:text-amber-400 hover:bg-amber-500/10"
                                >
                                  <FileText className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    // Load invoice data for download
                                    loadInvoice(invoice);
                                    setTimeout(() => {
                                      setFileName(`Invoice_${invoice.invoice_no}_${invoice.bill_to?.companyName || 'Client'}`);
                                      setShowDownloadDialog(true);
                                    }, 100);
                                  }}
                                  className="text-emerald-500 hover:text-emerald-400 hover:bg-emerald-500/10"
                                >
                                  <Download className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => deleteInvoice(invoice.id)}
                                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
        <Footer />
      </main>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-4xl max-h-[95vh] overflow-auto bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Invoice Preview</DialogTitle>
          </DialogHeader>
          <div className="overflow-auto" style={{ maxHeight: 'calc(95vh - 100px)' }}>
            <InvoicePreviewContent />
          </div>
        </DialogContent>
      </Dialog>

      {/* Download Dialog */}
      <Dialog open={showDownloadDialog} onOpenChange={setShowDownloadDialog}>
        <DialogContent className="sm:max-w-md bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Download className="w-5 h-5 text-amber-500" />
              Download Invoice
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Enter a name for your invoice file
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-slate-300">File Name</Label>
              <div className="flex items-center gap-2 mt-2">
                <Input
                  value={fileName}
                  onChange={(e) => setFileName(e.target.value)}
                  className="bg-slate-900/50 border-slate-600 text-white flex-1"
                  placeholder="Invoice_001"
                />
                <span className="text-slate-400">.pdf</span>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700"
                onClick={() => setShowDownloadDialog(false)}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-700 hover:to-amber-600"
                onClick={generatePDF}
                disabled={generating}
              >
                {generating ? 'Generating...' : 'Download PDF'}
              </Button>
            </div>
          </div>
          {/* Hidden preview for PDF generation */}
          <div style={{ position: 'absolute', left: '-9999px', top: '0' }}>
            <InvoicePreviewContent />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InvoiceGenerator;
